/**
 * @file Components/Admin/Users.jsx
 * @description Admin user management with real-time data from API.
 * Lists users with search, role toggle (admin/user), and deletion.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Search, Trash2, Shield, ShieldOff, Loader2, RefreshCw, Mail, Phone, Calendar, Users as UsersIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAllUsers, updateUserRole, deleteUser } from '../../services/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [togglingId, setTogglingId] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAllUsers();
      const list = res?.data?.data?.users || res?.data?.users || [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleToggleRole = async (user) => {
    const newIsAdmin = user.role !== 'admin';
    setTogglingId(user._id);
    try {
      await updateUserRole(user._id, { isAdmin: newIsAdmin });
      toast.success(`${user.name} is now ${newIsAdmin ? 'an admin' : 'a regular user'}`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update role: ' + (err?.response?.data?.message || err?.message));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete "${user.name}"? This action cannot be undone.`)) return;
    try {
      await deleteUser(user._id);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete: ' + (err?.response?.data?.message || err?.message));
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q ||
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q);
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, roleFilter]);

  const adminCount = users.filter(u => u.role === 'admin').length;
  const userCount = users.filter(u => u.role !== 'admin').length;

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin h-8 w-8 text-indigo-500" />
      </div>
    );
  }

  if (error && users.length === 0) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="text-red-500">{error}</p>
        <button onClick={fetchUsers} className="text-sm text-indigo-600 hover:underline flex items-center gap-1 mx-auto">
          <RefreshCw size={14} /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Total Users</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{users.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Admins</p>
          <p className="text-2xl font-bold text-purple-600 mt-1">{adminCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Regular Users</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{userCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder="Search by name, email, phone..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-72" />
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="all">All Roles</option>
            <option value="admin">Admins Only</option>
            <option value="user">Users Only</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-400">Auto-refreshes every 30s</p>
          <button onClick={fetchUsers} disabled={loading}
            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <UsersIcon size={36} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500">
                    {searchQuery || roleFilter !== 'all' ? 'No matching users found' : 'No users yet'}
                  </p>
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        user.role === 'admin' ? 'bg-purple-100' : 'bg-indigo-100'
                      }`}>
                        <span className={`font-semibold text-base ${
                          user.role === 'admin' ? 'text-purple-600' : 'text-indigo-600'
                        }`}>
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name || 'Unknown'}</p>
                        {user.username && <p className="text-xs text-gray-400">@{user.username}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-0.5">
                      <p className="text-sm text-gray-600 flex items-center gap-1.5">
                        <Mail size={12} className="text-gray-400" /> {user.email}
                      </p>
                      {user.phone && (
                        <p className="text-xs text-gray-400 flex items-center gap-1.5">
                          <Phone size={11} className="text-gray-300" /> {user.phone}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${
                      user.role === 'admin'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {user.role === 'admin' ? <Shield size={11} /> : null}
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                      <Calendar size={12} className="text-gray-400" />
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleToggleRole(user)}
                        disabled={togglingId === user._id}
                        className={`p-1.5 rounded-lg transition-all text-xs font-medium flex items-center gap-1 ${
                          user.role === 'admin'
                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                        } disabled:opacity-50`}
                        title={user.role === 'admin' ? 'Remove admin' : 'Make admin'}
                      >
                        {togglingId === user._id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : user.role === 'admin' ? (
                          <><ShieldOff size={14} /> Demote</>
                        ) : (
                          <><Shield size={14} /> Make Admin</>
                        )}
                      </button>
                      <button onClick={() => handleDelete(user)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete user">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Footer count */}
        {filteredUsers.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t text-xs text-gray-500">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;